import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { contactLocations, siteConfig } from "@/data/siteData";
import contactClock from "@/assets/icons/contact-clock.png";
import contactPhone from "@/assets/icons/contact-phone.png";
import contactMap from "@/assets/icons/contact-map.png";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function ContactSection() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const id = crypto.randomUUID();
    const company = formData.company || undefined;
    try {
      await supabase.functions.invoke("submit-contact-form", {
        body: {
          idempotencyKey: `contact-${id}`,
          templateData: { ...formData },
        },
      });
      // GTM submit event (in addition to the page-level lead_thank_you event)
      const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: "form_submit",
        form_name: "contact_us",
        form_id: id,
        company,
      });
      setFormData({ firstName: "", lastName: "", email: "", phone: "", company: "", message: "" });
      navigate(`/thank-you?source=contact_us&id=${id}`);
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-10 md:py-14 bg-background">
      <div className="container-main">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="section-label">
            Contact Us
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-3 uppercase">
            Speak With Our Team
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Speak with IntelliBPO to define your scope, operational objectives, and resourcing model. We deliver tailored solutions designed to scale efficiently and perform consistently.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-pale-pink rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Company Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <textarea
                  placeholder="Write your Message..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
                <Button type="submit" variant="default" size="lg" disabled={isSubmitting} className="uppercase text-xs font-semibold tracking-wide w-full">
                  {isSubmitting ? "Sending..." : "Request Proposal"}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info Card */}
          <div className="lg:col-span-2">
            <div className="bg-foreground rounded-2xl p-8 text-primary-foreground h-full flex flex-col">
              <div className="flex flex-col justify-between h-full gap-5">
                <div className="flex items-start gap-3">
                  <img src={contactClock} alt="Hours" className="h-12 w-12 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Hours</div>
                    <div className="text-sm text-primary-foreground/80">24/7 · 365 days a year</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <img src={contactPhone} alt="Phone" className="h-12 w-12 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Call Us</div>
                    <div className="text-sm text-primary-foreground/80">{siteConfig.contact.phone}</div>
                  </div>
                </div>
                {contactLocations.map((loc, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <img src={contactMap} alt="Location" className="h-12 w-12 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-sm">{loc.city}</div>
                      <div className="text-xs text-primary-foreground/80">{loc.address}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
